<section class="key-indicators" aria-label="Indicadores-chave da economia">
  <div class="key-indicators__header">
    <h3 class="key-indicators__title">Indicadores-chave da Economia</h3>

    <div class="key-indicators__actions">
      <label class="key-indicators__search" aria-label="Buscar indicador">
        <i class="pi pi-search"></i>
        <input
          type="search"
          placeholder="Buscar indicador"
          [value]="searchTerm()"
          (input)="updateSearchTerm($event)" />
        @if (hasSearchTerm()) {
          <button type="button" aria-label="Limpar busca" (click)="clearSearchTerm()">
            <i class="pi pi-times"></i>
          </button>
        }
      </label>

      <app-button
        icon="sliders-h"
        theme="white"
        size="small"
        [pureIcon]="true"
        [noMargin]="true"
        [rounded]="true"
        title="Configurar ordem dos indicadores"
        (sendClick)="indicatorOrderSettings.toggle($event)">
      </app-button>
      <app-button
        icon="refresh"
        theme="white"
        size="small"
        [pureIcon]="true"
        [noMargin]="true"
        [rounded]="true"
        [title]="'Atualizar fontes oficiais'"
        [isLoading]="isRefreshing()"
        (sendClick)="loadKeyIndicators(true)">
      </app-button>
    </div>

    <p-popover #indicatorOrderSettings styleClass="key-indicators-order-popover">
      <div class="key-indicators-order">
        <div class="key-indicators-order__header">
          <strong>Ordem dos indicadores</strong>
          <span>Arraste pela linha para ajustar a ordem.</span>
        </div>
        <div
          class="key-indicators-order__list"
          cdkDropList
          [cdkDropListData]="orderedIndicators()"
          (cdkDropListDropped)="dropIndicatorOrder($event)">
          @for (indicator of orderedIndicators(); track indicator.code) {
            <div class="key-indicators-order__item" cdkDrag>
              <span class="key-indicators-order__drag-icon">
                <i class="pi pi-bars"></i>
              </span>
              <span>{{ getIndicatorName(indicator) }}</span>
            </div>
          }
        </div>
        <div class="key-indicators-order__footer">
          <span>{{ orderedIndicators().length }} indicadores</span>
          <button type="button" (click)="resetIndicatorOrder()">Restaurar padrão</button>
        </div>
      </div>
    </p-popover>
  </div>

  @if (keyIndicators()?.lastSuccessfulUpdateAtGmt) {
    <div class="key-indicators__updated">
      <span class="key-indicators__updated-dot"></span>
      Atualizado {{ formatShortDateTime(keyIndicators()!.lastSuccessfulUpdateAtGmt) }}
    </div>
  }

  @if (isLoading()) {
    <div class="key-indicators__loading">
      <app-cog-loading-spinner [size]="48"></app-cog-loading-spinner>
    </div>
  } @else if (hasError()) {
    <div class="key-indicators__empty">
      <span>Não foi possível carregar os indicadores.</span>
      <app-button
        label="Tentar novamente"
        icon="refresh"
        theme="white"
        size="small"
        (sendClick)="loadKeyIndicators()">
      </app-button>
    </div>
  } @else {
    @if (listIndicators().length) {
      <div
        class="key-indicators__list"
        [class.key-indicators__list--scrollable]="shouldShowOverflowIndicators()">
        @for (indicator of listIndicators(); track indicator.code) {
          @let config = getIndicatorConfig(indicator);
          <div class="key-indicators__row">
            <span class="key-indicators__row-icon key-indicators__row-icon--{{ config.theme }}">
              <i [class]="config.icon"></i>
            </span>
            <div class="key-indicators__row-body">
              <div class="key-indicators__row-name">{{ getIndicatorName(indicator) }}</div>
              <div class="key-indicators__row-sub">{{ config.caption }}</div>
            </div>
            <div class="key-indicators__row-value">
              <strong>{{ indicator.formattedValue || '-' }}</strong>
              <span>{{ getIndicatorTimeLabel(indicator) }}</span>
            </div>
            @if (hasSourceDetails(indicator)) {
              <button
                type="button"
                class="key-indicators__source-trigger"
                [attr.aria-label]="'Ver detalhes da fonte de ' + getIndicatorName(indicator)"
                (click)="toggleSourcePopover($event, sourcePopover)">
                <i class="pi pi-info-circle"></i>
              </button>

              <p-popover
                #sourcePopover
                styleClass="key-indicators-source-popover"
                [appendTo]="'body'"
                (onHide)="onSourcePopoverHide(sourcePopover)">
                <div class="key-indicators-source">
                  <div class="key-indicators-source__header">
                    <span
                      class="key-indicators-source__icon key-indicators-source__icon--{{
                        config.theme
                      }}">
                      <i [class]="config.icon"></i>
                    </span>
                    <div class="key-indicators-source__heading">
                      <span>Detalhes da fonte</span>
                      <strong>{{ getIndicatorName(indicator) }}</strong>
                      <small>{{ getCompactSourceLabel(indicator) }}</small>
                    </div>
                  </div>

                  <div class="key-indicators-source__list">
                    @for (detail of getSourceDetails(indicator); track detail.label) {
                      <div
                        class="key-indicators-source__item"
                        [class.key-indicators-source__item--status]="detail.label === 'Status'">
                        <span>{{ detail.label }}</span>
                        <strong [class.key-indicators-source__status]="detail.label === 'Status'">
                          {{ detail.value }}
                        </strong>
                      </div>
                    }
                  </div>

                  @if (indicator.officialPageUrl || indicator.apiUrl) {
                    <div class="key-indicators-source__links">
                      @if (indicator.officialPageUrl) {
                        <a
                          [href]="indicator.officialPageUrl"
                          target="_blank"
                          rel="noopener noreferrer">
                          Página oficial
                          <i class="pi pi-external-link"></i>
                        </a>
                      }
                      @if (indicator.apiUrl) {
                        <a [href]="indicator.apiUrl" target="_blank" rel="noopener noreferrer">
                          URL da API
                          <i class="pi pi-external-link"></i>
                        </a>
                      }
                    </div>
                  }
                </div>
              </p-popover>
            }
          </div>
        }
      </div>
    } @else {
      <div class="key-indicators__empty key-indicators__empty--compact">
        <span>Nenhum indicador encontrado.</span>
        <app-button
          label="Limpar busca"
          icon="times"
          theme="white"
          size="small"
          (sendClick)="clearSearchTerm()">
        </app-button>
      </div>
    }

    @if (canToggleIndicators()) {
      <button type="button" class="key-indicators__expand" (click)="toggleIndicators()">
        {{ showAllIndicators() ? 'Voltar à visão personalizada' : 'Ver todos os indicadores' }}
        <i [class]="showAllIndicators() ? 'pi pi-angle-up' : 'pi pi-angle-right'"></i>
      </button>
    }
  }
</section>
